import Cookies from 'js-cookie';
import {
  ArticlePageQuery,
  CategoryPageQuery,
  HomePageQuery,
  Language,
} from '@elevio-kb/queries/dist/types';
import { parseUrl } from 'query-string';

export type InitialData = {
  article?: ArticlePageQuery;
  category?: CategoryPageQuery;
  home?: HomePageQuery;
  error?: { extensions: { code: string } };
};

declare global {
  interface Window {
    template: string;
    baseUrl: string;
    companyUid: string;
    companyName: string;
    languageId: string;
    localisationData: object;
    customLocalisationData: object;
    cname: string;
    subdomain: string;
    ssoEnabled: boolean;
    ssoForced: boolean;
    supportedLanguages: Array<Language>;
    initialData?: InitialData;
    searchAsYouTypeEnabled: boolean;
    whitelabelEnabled: boolean;
    feedbackEnabled: boolean;
    feedbackEmailFieldHidden: boolean;
    agentLoginEnabled: boolean;
    companyUrl: string;
    jsSnippetUrl?: string;
  }
}

export type User = {
  firstName?: string;
  lastName?: string;
  email?: string;
  hash?: string;
  uid?: string;
};

type JWTPayload = {
  iss: string;
  sub: string;
  exp: number;
  iat: number;
  jti: string;
  userName: string;
  scope: string[];
  traits: Traits;
};

type Traits = {
  firstName: string;
  lastName: string;
  groups: string[];
  userUid: string;
};

let user: User;

function extractUser(): User {
  const tokenName = getTokenName(window.cname, window.subdomain);
  const ssoToken = Cookies.get(tokenName);

  if (ssoToken) {
    const payload = ssoToken.split('.')[1];

    const parsedPayload: JWTPayload = JSON.parse(window.atob(payload));

    const {
      userName: email,
      traits: { userUid: uid },
    } = parsedPayload;

    return {
      email,
      uid,
    };
  } else {
    return {};
  }
}

export function getInitialData(): InitialData | undefined {
  return window.initialData;
}

export function getUser(): User {
  if (user) return user;
  user = extractUser();
  return user;
}

export function getCompanyUid(): string {
  return window.companyUid;
}

export function getCompanyName(): string {
  return window.companyName;
}

export function getCompanyUrl(): string {
  return window.companyUrl;
}

export function getJSSnippetUrl(): string | undefined {
  return window.jsSnippetUrl;
}

export function getAgentLoginEnabled(): boolean {
  return window.agentLoginEnabled;
}

export function getLanguageId(): string {
  return window.languageId || 'en'; // the default is just to make testing easier
}

/**
 * This is the language id with the version specifier uppercased
 * ie en-us becomes en-US
 * */

export function getFormattedLanguageId(): string {
  const parts = getLanguageId().split('-');
  if (parts.length > 1) {
    return `${parts[0]}-${parts[1].toUpperCase()}`;
  }
  return parts[0];
}

export function getLocalisationData(): object {
  return { ...window.localisationData, ...window.customLocalisationData };
}

export function getCanonicalBaseUrl(): string {
  return window.baseUrl;
}

export function getFeedbackEnabled(): boolean {
  return window.feedbackEnabled;
}

export function getFeedbackEmailFieldHidden(): boolean {
  return window.feedbackEmailFieldHidden;
}

export function getSsoUrl(): string {
  return '/sso/login';
}

export function getAgentLoginUrl(): string {
  return '/sso/agent/login';
}

export function getSsoEnabled(): boolean {
  return window.ssoEnabled;
}

export function getSsoForced(): boolean {
  return window.ssoForced;
}

export const getTokenName = (cname?: string, subdomain?: string) =>
  `${cname ? `${cname}-` : ''}${subdomain ? `${subdomain}-` : ''}elevio_token`;

export function getTokenFromCookies(): string | undefined {
  const tokenName = getTokenName(window.cname, window.subdomain);
  return Cookies.get(tokenName);
}

export function getSupoportedLanguages(): Array<Language> {
  return window.supportedLanguages;
}

export function getBaseUrl(): string {
  return `/${getLanguageId()}`;
}

export function getSearchQuery(): string {
  const parsedUrl = parseUrl(location.search);
  const query = (parsedUrl.query['query'] as string) || '';
  return query;
}

export function getSearchAsYouTypeEnabled(): boolean {
  return !!window.searchAsYouTypeEnabled;
}

export function getIsCypressMode(): boolean {
  return !!(window as any).Cypress;
}
